import localStorageProvider from "dataProvidersDeprecated/localStorageProvider";
import { isBefore, subHours } from "date-fns";
import { parseDate } from "helpers";
import React, { useContext } from "react";
import { useActiveCampaigns } from "redux/campaigns/useCampaigns";
import { useCampaignsStatusDataLoader } from "redux/campaigns/useCampaignsStatus";
import useCampaignsBudgetsMap from "redux/campaignsBudgets/useCampaignsBudgetsMap";
import { TimestampList } from "routes/Campaigns/components/CampaignRiskNotifications/CampaignRiskNotifications";
import useNotifications from "./useNotifications/useNotifications";

const AppNotificationsContext = React.createContext<any>({});

export const useAppNotifications = () => useContext(AppNotificationsContext);

export const AppNotificationProvider = ({ children }) => {
  useCampaignsStatusDataLoader();
  useCampaignsBudgetsMap();
  useActiveCampaigns();

  const [notificationsDisplaySetting, setNotificationsDisplaySetting] =
    React.useReducer(
      (state, { newDisplaySetting, shouldStore = false, remove = null }) => {
        const resultState = { ...state, ...newDisplaySetting };

        if (remove) {
          const c = structuredClone(
            localStorageProvider.getRiskNotificationListTimestamp() || {}
          );
          delete c[remove as any];

          localStorageProvider.setRiskNotificationListTimestamp(c);
          delete resultState[remove];
        }

        if (shouldStore) {
          const newTimestampEntries = Object.entries(newDisplaySetting).reduce(
            (acc, [nId, isDisplay]) => {
              if (!isDisplay) acc[nId] = Date.now();
              return acc;
            },
            {}
          );

          localStorageProvider.setRiskNotificationListTimestamp({
            ...(localStorageProvider.getRiskNotificationListTimestamp() || {}),
            ...newTimestampEntries,
          });
        }

        return resultState;
      },
      {}
    );
  const hide = (nId) =>
    setNotificationsDisplaySetting({
      newDisplaySetting: { [nId]: false },
      shouldStore: true,
    });

  const unhide = (nId) =>
    setNotificationsDisplaySetting({
      newDisplaySetting: {},
      shouldStore: true,
      remove: nId,
    });

  const notifications = useNotifications();
  const visibleNotifications = React.useMemo(
    () =>
      notifications.filter((n) => notificationsDisplaySetting[n.id] !== false),
    [notifications, notificationsDisplaySetting]
  );

  React.useEffect(() => {
    let isMounted = true;
    const timestampList: TimestampList =
      localStorageProvider.getRiskNotificationListTimestamp();
    if (timestampList) {
      const storedNotificationsDisplaySetting = Object.entries(
        timestampList
      ).reduce((acc, [nId, timestamp]) => {
        acc[nId] =
          !timestamp ||
          isBefore(parseDate(timestamp) as Date, subHours(new Date(), 4));
        return acc;
      }, {});

      isMounted &&
        setNotificationsDisplaySetting({
          newDisplaySetting: storedNotificationsDisplaySetting,
        });
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const dismissedNotifications = React.useMemo(() => {
    return notifications.reduce((acum: any[], curr: any) => {
      return visibleNotifications.find((n) => n.id === curr.id)
        ? acum
        : [...acum, curr];
    }, []);
  }, [notifications, visibleNotifications]);

  return (
    <AppNotificationsContext.Provider
      value={{
        hide,
        unhide,
        notifications,
        dismissedNotifications,
        visibleNotifications,
        setNotificationsDisplaySetting,
        notificationsDisplaySetting,
      }}>
      {children}
    </AppNotificationsContext.Provider>
  );
};
